import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { handleSignIn, handleSignOut, handleRefreshToken, getIdentity, isAuthenticated, initializeMsal } from '../ServiceRequest';

interface User {
    name: string | null;
    username: string | null;
}

interface AuthContextProps {
    user: User | null;
    isAuthenticated: boolean;
    isLoadingAuth: boolean;
    login: (callbackUrl?: string) => Promise<void>;
    logout: () => Promise<void>;
    refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [authState, setAuthState] = useState<boolean>(false);
    const [isLoadingAuth, setIsLoadingAuth] = useState(true);


    const loadAuthData = () => {
        if (isAuthenticated()) {
            const identity = getIdentity();
            if (identity) {
                setUser({
                    name: identity.name ?? null,
                    username: identity.username ?? null,
                });
                setAuthState(true);
            } else {
                setUser(null);
                setAuthState(false);
            }
        } else {
            setUser(null);
            setAuthState(false);
        }
    };



     useEffect(() => {
        const initialize = async () => {
            await initializeMsal();
            loadAuthData();
            setIsLoadingAuth(false);
        };

        initialize();
    }, []);

    const login = async (callbackUrl?: string) => {
        await handleSignIn(callbackUrl);
        loadAuthData();
    };

    const logout = async () => {
        await handleSignOut();
        setUser(null);
        setAuthState(false);
    };

    const refreshToken = async () => {
        await handleRefreshToken();
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: authState,
                login,
                logout,
                refreshToken,
                isLoadingAuth
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextProps => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
